import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import type { FC, ReactNode } from "react";
import { ConfigurationAssistantChat } from "./configurationAssistant/configurationAssistantChat.js";
import { ConfigurationAssistantContextProvider } from "./configurationAssistant/configurationAssistantContext.js";
import type { TeammateAgentConfigController } from "./useTeammateAgentConfigController.js";

const VOICE_AGENT_SUGGESTIONS = [
  "Simulate common FAQs from my knowledge base",
  "Help me draft updates to my knowledge base",
  "Review my agent configuration for any gaps or issues",
  "Add a step to qualify leads before transferring",
  "What are the top drivers of unresolved calls?",
] as const;

export interface AgentConfigAssistantSidebarProps {
  controller: TeammateAgentConfigController;
  /** Optional content rendered just above the composer (e.g. a suggestion list). */
  aboveComposer?: ReactNode;
}

/**
 * The docked "AI Assistant" panel shared across the agent-config surfaces. It
 * keeps the same chat session (provided at the page level) while the tab
 * content changes around it.
 */
export const AgentConfigAssistantSidebar: FC<
  AgentConfigAssistantSidebarProps
> = ({ controller, aboveComposer }) => {
  const {
    handleApplyOperation,
    playbook,
    testSpecs,
    knowledgeBaseAttachments,
  } = controller;

  return (
    <div className="flex h-full w-[350px] flex-col border-l border-border-light bg-background">
      <div className="border-b border-border-light bg-white px-4 py-3">
        <Label size={Size.SMALL}>AI Assistant</Label>
      </div>
      <div className="flex min-h-0 flex-1 flex-col">
        <ConfigurationAssistantContextProvider
          onApplyOperation={handleApplyOperation}
          playbookTree={playbook.data?.tree}
          testSpecs={testSpecs}
          outcomeDefinition={playbook.data?.outcome ?? undefined}
          knowledgeBaseAttachments={knowledgeBaseAttachments}
        >
          <ConfigurationAssistantChat
            title="Build your agent with AI"
            description="I can analyze calls, draft updates, and test your agent's performance. All changes need your approval before going live."
            placeholder="Describe what you want to change..."
            suggestions={VOICE_AGENT_SUGGESTIONS}
            aboveComposer={aboveComposer}
          />
        </ConfigurationAssistantContextProvider>
      </div>
    </div>
  );
};
